import { useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAdminMenu, invalidateAdminMenu } from "../../hooks/useAdminMenu";
import { useAdminCategories } from "../../hooks/useAdminCategories";
import useAlert from "../../hooks/useAlert";
import useApi from "../../hooks/useApi";
import useMenuImageUpload, { ALLOWED_IMAGE_TYPES } from "../../hooks/useMenuImageUpload";
import { useLoaderStore } from "../../context";
import { formatCents } from "../../utils/money";
import { API } from "../../utils/constants";
import { TextInput, TextAreaInput, SelectInput, ActiveToggle } from "../../components/Inputs";


// ── Dollar-to-cents input helper ──────────────────────────────────────────

/** Converts a dollar display string to integer cents. */
const dollarsToCents = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
};

/** Converts integer cents to a dollar display string. */
const centsToDollars = (cents) => (cents / 100).toFixed(2);

// ── Menu item image uploader (SAS client-direct, BLU-007) ──────────────────

/** File-picker accept string for the image input. */
const IMAGE_ACCEPT = ALLOWED_IMAGE_TYPES.join(",");

/**
 * MenuImageUploader — replaces the old free-text Image URL field with a real
 * uploader implementing the BLU-007 SAS flow (SPR-009, CON-002 §6):
 *   pick file → request write-only SAS → PUT bytes to Azure → persist blobUrl.
 *
 * A brand-new item has no `{id}` yet, so the SAS flow can't run until the item
 * exists; in create mode we show a hint to save first, then upload on edit.
 *
 * @param {object} props
 * @param {number|null} props.itemId    - menu item id (null in create mode)
 * @param {string|null} props.imageUrl  - the current persisted image URL
 * @param {Function} props.onChange     - (newUrl: string|null) => void after persist
 */
function MenuImageUploader({ itemId, imageUrl, onChange }) {
  const alert = useAlert();
  const fileInputRef = useRef(null);
  const { uploadImage, removeImage, uploading } = useMenuImageUpload();

  const canUpload = Boolean(itemId);

  const handlePick = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !itemId) return;

    const res = await uploadImage(itemId, file);
    if (res.error) {
      alert("Upload failed", res.error, "error");
      return;
    }
    onChange(res.imageUrl);
    await invalidateAdminMenu();
    alert("Image updated", "The item image was uploaded.", "success");
  };

  const handleRemove = async () => {
    if (!itemId) return;
    const res = await removeImage(itemId);
    if (res.error) {
      alert("Remove failed", res.error, "error");
      return;
    }
    onChange(null);
    await invalidateAdminMenu();
    alert("Image removed", "The item image was removed.", "success");
  };

  return (
    <div data-testid="menu-image-uploader">
      <p className="block font-heading tracking-widest text-teal-deep text-sm mb-1">
        ITEM IMAGE
      </p>

      <div className="flex items-start gap-4">
        {/* Thumbnail preview / placeholder */}
        <div className="w-24 h-24 shrink-0 rounded-xl border-2 border-charcoal overflow-hidden bg-charcoal flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Current item"
              className="w-full h-full object-cover"
              data-testid="image-thumbnail"
            />
          ) : (
            <span className="font-display text-aluminum/40 text-3xl" aria-hidden="true">
              ☕
            </span>
          )}
        </div>

        <div className="flex-1">
          {canUpload ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_ACCEPT}
                onChange={handleFile}
                className="hidden"
                data-testid="image-file-input"
                aria-label="Choose item image"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handlePick}
                  disabled={uploading}
                  data-testid="image-upload-button"
                  className="font-heading tracking-wider text-xs text-teal border-2 border-teal px-3 py-1.5 rounded-full hover:bg-teal hover:text-white transition-colors disabled:opacity-40"
                >
                  {uploading ? "WORKING…" : imageUrl ? "REPLACE" : "UPLOAD IMAGE"}
                </button>
                {imageUrl && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    disabled={uploading}
                    data-testid="image-remove-button"
                    className="font-heading tracking-wider text-xs text-rust border-2 border-rust px-3 py-1.5 rounded-full hover:bg-rust hover:text-white transition-colors disabled:opacity-40"
                  >
                    REMOVE
                  </button>
                )}
              </div>
              <p className="font-body text-ink/50 text-xs mt-2">
                JPEG, PNG, or WebP · up to 5 MB.
              </p>
            </>
          ) : (
            <p
              className="font-body text-ink/60 text-sm"
              data-testid="image-save-first-hint"
            >
              Save the item first, then edit it to upload an image.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Menu item form modal ──────────────────────────────────────────────────

/**
 * MenuItemModal — Create or edit a menu item.
 * @param {object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {object|null} props.item - If set, form is in edit mode
 * @param {Array} props.categories - AdminCategoryDto[] for the select dropdown
 */
function MenuItemModal({ open, onClose, item = null, categories = [] }) {
  const isEdit = !!item;
  const alert = useAlert();
  const { post, put } = useApi();
  const { setShowLoader } = useLoaderStore();

  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [categoryId, setCategoryId] = useState(
    item?.categoryId ? { value: item.categoryId, label: item.categoryName } : null
  );
  const [priceDisplay, setPriceDisplay] = useState(
    item ? centsToDollars(item.priceCents) : ""
  );
  const [imageUrl, setImageUrl] = useState(item?.imageUrl ?? "");

  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));

  const validate = () => {
    if (!name.trim()) { alert("Validation", "Name is required.", "warning"); return false; }
    if (!categoryId) { alert("Validation", "Category is required.", "warning"); return false; }
    if (!priceDisplay || isNaN(parseFloat(priceDisplay))) {
      alert("Validation", "Enter a valid price.", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setShowLoader(true);

    // imageUrl is managed by the dedicated image endpoints (BLU-007 SAS flow),
    // not this create/update payload.
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      categoryId: categoryId.value,
      priceCents: dollarsToCents(priceDisplay),
    };

    try {
      if (isEdit) {
        await put(API.ADMIN.MENU.UPDATE(item.id), payload);
        alert("Updated", `"${name}" updated successfully.`, "success");
      } else {
        await post(API.ADMIN.MENU.CREATE, payload);
        alert("Created", `"${name}" added to the menu.`, "success");
      }
      await invalidateAdminMenu();
      onClose();
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to save item.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-md bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6"
                data-testid="menu-item-modal"
              >
                <div className="flex items-center justify-between mb-5">
                  <Dialog.Title className="font-display text-2xl text-charcoal uppercase">
                    {isEdit ? "EDIT ITEM" : "ADD ITEM"}
                  </Dialog.Title>
                  <button onClick={onClose} aria-label="Close modal" className="text-charcoal/40 hover:text-charcoal">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <TextInput
                    label="Name *"
                    name="item-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <SelectInput
                    label="Category *"
                    name="item-category"
                    options={categoryOptions}
                    value={categoryId}
                    onChange={setCategoryId}
                  />
                  <TextInput
                    label="Price ($) *"
                    name="item-price"
                    value={priceDisplay}
                    onChange={(e) => setPriceDisplay(e.target.value)}
                    placeholder="4.50"
                  />
                  <TextAreaInput
                    label="Description"
                    name="item-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                  <MenuImageUploader
                    itemId={item?.id ?? null}
                    imageUrl={imageUrl}
                    onChange={setImageUrl}
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSubmit}
                    className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                    data-testid="modal-submit-button"
                  >
                    {isEdit ? "SAVE CHANGES" : "ADD TO MENU"}
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Delete confirmation dialog ─────────────────────────────────────────────

function DeleteConfirmDialog({ open, onClose, onConfirm, itemName }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-sm bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6"
              data-testid="delete-confirm-dialog"
            >
              <Dialog.Title className="font-display text-2xl text-charcoal uppercase mb-3">
                DELETE ITEM
              </Dialog.Title>
              <p className="font-body text-ink/70 mb-6">
                Delete <span className="font-semibold text-charcoal">"{itemName}"</span>? This is permanent and cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onConfirm}
                  className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                  data-testid="confirm-delete-button"
                >
                  DELETE
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── MenuAdmin page ────────────────────────────────────────────────────────

/**
 * MenuAdmin — Admin menu item management (CRUD).
 *
 * Features:
 * - Item list with Active toggle (PUT /api/admin/menu/{id})
 * - Create item modal (POST /api/admin/menu)
 * - Edit item modal (PUT /api/admin/menu/{id})
 * - Delete item with confirmation (DELETE /api/admin/menu/{id})
 * - Price stored as INT cents; displayed via formatCents()
 */
export default function MenuAdmin() {
  const alert = useAlert();
  const { put, del } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { items, isLoading, error } = useAdminMenu();
  const { categories } = useAdminCategories();

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Bumped on every open so the modal remounts with fresh state — guarantees
  // blank-on-create, prefilled-on-edit, and cleared-on-success (FE-3).
  const [modalSession, setModalSession] = useState(0);

  const openCreate = () => { setEditItem(null); setModalSession((n) => n + 1); setModalOpen(true); };
  const openEdit = (item) => { setEditItem(item); setModalSession((n) => n + 1); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditItem(null); };

  const handleToggleActive = async (item) => {
    setShowLoader(true);
    try {
      await put(API.ADMIN.MENU.UPDATE(item.id), { isActive: !item.isActive });
      await invalidateAdminMenu();
      alert("Updated", `"${item.name}" ${!item.isActive ? "activated" : "deactivated"}.`, "success");
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to update item.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setShowLoader(true);
    try {
      await del(API.ADMIN.MENU.DELETE(deleteTarget.id));
      await invalidateAdminMenu();
      alert("Deleted", `"${deleteTarget.name}" permanently deleted.`, "success");
      setDeleteTarget(null);
    } catch (err) {
      alert("Error", err?.data?.detail || "Failed to delete item.", "error");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <main data-testid="admin-menu-page" className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="font-heading tracking-[0.3em] text-teal-deep text-sm">ADMIN</p>
          <h1 className="font-display text-4xl text-charcoal uppercase">Menu Management</h1>
          <p className="font-body text-ink/70 text-sm mt-1">
            Add, edit, or deactivate menu items.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-rust text-white font-heading tracking-widest px-5 py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
          data-testid="add-item-button"
        >
          + ADD ITEM
        </button>
      </div>

      {/* States */}
      {isLoading && (
        <p className="font-body text-ink/60">Loading menu items…</p>
      )}
      {error && (
        <p className="font-body text-rust">Unable to load menu items.</p>
      )}

      {/* Item table */}
      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full border-2 border-charcoal rounded-2xl overflow-hidden">
            <thead className="bg-charcoal text-white">
              <tr>
                {["Name", "Category", "Price", "Active", "Actions"].map((h) => (
                  <th key={h} className="font-heading tracking-wider text-left px-4 py-3 text-sm">
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-10 font-body text-ink/50">
                    No menu items yet. Add one above.
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-line hover:bg-cream-deep/50 transition-colors"
                  data-testid={`menu-row-${item.id}`}
                >
                  <td className="px-4 py-3 font-body text-ink">{item.name}</td>
                  <td className="px-4 py-3 font-body text-ink/70 text-sm">{item.categoryName}</td>
                  <td className="px-4 py-3 font-display text-teal text-lg" data-testid={`row-price-${item.id}`}>
                    {formatCents(item.priceCents)}
                  </td>
                  <td className="px-4 py-3">
                    <ActiveToggle
                      enabled={item.isActive}
                      onToggle={() => handleToggleActive(item)}
                      label={`Toggle ${item.name} active`}
                      testId={`toggle-active-${item.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(item)}
                        className="font-heading tracking-wider text-xs text-teal border-2 border-teal px-3 py-1 rounded-full hover:bg-teal hover:text-white transition-colors"
                        data-testid={`edit-item-${item.id}`}
                      >
                        EDIT
                      </button>
                      <button
                        onClick={() => setDeleteTarget(item)}
                        className="font-heading tracking-wider text-xs text-rust border-2 border-rust px-3 py-1 rounded-full hover:bg-rust hover:text-white transition-colors"
                        data-testid={`delete-item-${item.id}`}
                      >
                        DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal — keyed so each open starts from fresh state */}
      <MenuItemModal
        key={`${editItem ? `edit-${editItem.id}` : "create"}-${modalSession}`}
        open={modalOpen}
        onClose={closeModal}
        item={editItem}
        categories={categories}
      />

      {/* Delete confirmation */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.name ?? ""}
      />
    </main>
  );
}
